import { initializeApp } from "firebase/app";
import {
    getAuth,
    initializeAuth,
    onAuthStateChanged,
    browserLocalPersistence,
    indexedDBLocalPersistence,
    browserSessionPersistence,
    browserPopupRedirectResolver,
} from "firebase/auth";
import { startAuthStartupWatch } from "../lib/auth/authStartupWatch";

// SECURITY: Firebase config from environment variables only - no hardcoded fallbacks
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Validate required config before initializing
const requiredKeys = ['apiKey', 'authDomain', 'projectId'] as const;
for (const key of requiredKeys) {
    if (!firebaseConfig[key]) {
        console.error(`Missing required Firebase config: REACT_APP_FIREBASE_${key.toUpperCase().replace(/([A-Z])/g, '_$1').toUpperCase()}`);
    }
}

const app = initializeApp(firebaseConfig);

/**
 * THE SIGNED-IN USER IS KEPT IN ONE PLACE, NAMED HERE (2026-09-23).
 *
 * Opening a second prest0.ai tab used to sign every other open tab out for
 * about a second. Measured on the live bundle: the first tab logged
 * `reset_session_begin reason: sign_out`, `signed_out`, then `signed_in`
 * again 0.8 s later. That sign-out ran the full reset in lib/auth/session.ts,
 * which clears the local thread copies, the composer draft and the other
 * per-account caches in the storage both tabs share.
 *
 * The cause was two opposite choices about where the user is stored.
 * `getAuth(app)` prefers the browser's database (IndexedDB) and moves a user
 * it finds in local storage there, deleting the local copy. session.ts then
 * called `setPersistence(browserLocalPersistence)`, which moved the user back.
 * So every page load moved the stored user out of local storage and then put
 * it back, with a network round trip in between. Every other tab watches that
 * local-storage key, saw it deleted, and signed out until it reappeared.
 *
 * So the order is stated once, at the app's birth, before anything can call
 * `getAuth()` (a caller needs this app, and this module creates it): local
 * storage first (where every signed-in user already is), then the browser's
 * database, then the per-tab session store for a browser without either. A
 * user found in the first store is never moved, so nothing any other tab
 * watches changes on boot. `getAuth()` everywhere else returns this instance.
 * The popup/redirect resolver is the same one `getAuth` supplies, so Google
 * sign-in by popup or redirect is unchanged.
 *
 * ONLY WHERE THE BROWSER BUILD IS LOADED. This import also reaches the SDK's
 * SERVER build: jest 27 ignores the package's `exports` map and loads its
 * `main` (`dist/node/index.js`), and so would any server-side import of this
 * module. There the popup/redirect resolver is an error object, not a class,
 * so `initializeAuth` refused it: the SDK logged `INTERNAL ASSERTION FAILED:
 * Expected a class definition` and threw into the catch below, in 45 unit
 * suites on every run. The server build has no browser storage to name, and
 * `getAuth()` serves it with its own defaults, as it did before this block
 * existed. In the browser build the resolver IS a class
 * (`BrowserPopupRedirectResolver`), so the call always runs there. Measured on
 * prest0.ai: a stored user stays in local storage and the browser database
 * holds none, which only this order produces.
 *
 * Proof: tests/auth/authStoreOrder.test.ts pins the order and the boot path,
 * tests/auth/authInitServerBuildIsQuiet.test.ts runs this module on the real
 * server build, and scripts/probe-second-tab-auth.mjs runs the real SDK in two
 * Chromium tabs. The probe shows the old order signing the first tab out and
 * this order leaving it signed in.
 */
if (firebaseConfig.apiKey && typeof browserPopupRedirectResolver === 'function') {
    try {
        initializeAuth(app, {
            persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
            popupRedirectResolver: browserPopupRedirectResolver,
        });
    } catch {
        // Already initialized (this module evaluated twice under hot reload):
        // getAuth(app) returns the existing instance, which carries this order.
    }
    // The sign-in start never waits in silence (2026-10-03): if it has not answered after 10 s and Firebase's own
    // database is held by another tab's pending delete, a notice says which step clears it (lib/auth/authStartupWatch.ts).
    // Only where there is a browser database to be held.
    if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
        try {
            startAuthStartupWatch({
                onAnswer: (answered) => onAuthStateChanged(getAuth(app), () => answered()),
            });
        } catch {
            /* the watch must never affect the sign-in it watches */
        }
    }
}

export default app;